import { useNavigate } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as yup from "yup";
import { HashLoader } from "react-spinners";
import useVerifyNIN from "../../hooks/useVerifyNIN";
import SignUpNavbar from "../../components/SignUpNavbar";
import GetStartedModal from "../../components/GetStartedModal";
import { useAuth } from "../../context/AuthContext";

const schema = yup.object().shape({
  nin_number: yup
    .string()
    .required("NIN is required")
    .length(11, "NIN must be exactly 11 digits"),
});

const VerifyNIN = () => {
  const navigate = useNavigate();
  const { user, refetch } = useAuth();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });

    if (user?.compliance?.nin_verified) {
      navigate("/home", { replace: true });
    }
  }, [user, navigate]);

  const { isVerifyingNINError, verifyNIN, verifyingNIN, verifyingNINError } =
    useVerifyNIN({
      async onSuccess(data) {
        // Refresh the cached profile so `compliance.nin_verified` is current
        // before the onboarding gate re-evaluates — prevents bouncing a
        // just-verified user back to this page.
        await refetch();
        navigate("/sign-up/complete-profile", {
          state: {
            nin_data: data.data,
          },
        });
      },
    });

  const isLoading = useMemo(() => verifyingNIN, [verifyingNIN]);
  const isError = useMemo(() => isVerifyingNINError, [isVerifyingNINError]);
  const error = useMemo(() => verifyingNINError, [verifyingNINError]);

  return (
    <div>
      {isLoading && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      <Formik
        initialValues={{
          nin_number: "",
        }}
        validationSchema={schema}
        onSubmit={(values) => {
          if (user?.compliance?.nin_verified) {
            navigate("/home", { replace: true });
            return;
          }
          verifyNIN(values);
        }}
      >
        {({ isValid, dirty }) => (
          <div>
            <GetStartedModal />
            <SignUpNavbar />
            <div className="flex flex-col justify-center items-center p-10 px-5 md:px-20 gap-8 text-lg">
              <div className="flex flex-col justify-center items-center gap-3">
                <div className="text-3xl md:text-3xl lg:text-5xl max-w-3xl font-bold text-center text-deepBlue">
                  Verify your National Identity Number (NIN)
                </div>
                <div className="text-gray-600 text-center max-w-lg space-y-2">
                  <p>
                    To get started with your Visaro wallet and unlock access to
                    personalized credit options, we need to verify your
                    identity.
                  </p>
                </div>
              </div>

              <Form className="w-full flex-col gap-5 flex items-center justify-center text-base">
                <div className="flex flex-col gap-2 max-w-md w-full">
                  <label
                    htmlFor="nin_number"
                    className="text-sm font-bold text-gray-700"
                  >
                    National Identity Number*
                  </label>
                  <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                    <Field
                      type="text"
                      id="nin_number"
                      name="nin_number"
                      placeholder="Enter your 11-digit NIN"
                      className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
                    />
                  </div>
                  <ErrorMessage
                    name="nin_number"
                    component="div"
                    className="text-red-500"
                  />
                </div>

                <div className="flex flex-col gap-2 max-w-md w-full">
                  {isError && (
                    <div className="text-red-500 text-left text-sm">
                      {error?.message?.toLowerCase().includes("already been attached") ||
                      error?.message?.toLowerCase().includes("already in use") ? (
                        <span>
                          This NIN is already linked to another Visaro account. If
                          this is your NIN, please contact support or try signing in
                          with a different email.
                        </span>
                      ) : (
                        error?.message
                      )}
                    </div>
                  )}
                  <button
                    disabled={!isValid || !dirty}
                    type="submit"
                    className={`p-4 px-5 w-full border rounded-lg font-bold text-white text-sm ${
                      isValid && dirty
                        ? "bg-deepBlue hover:bg-blue-700"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    {isLoading ? "Verifying..." : "Continue"}
                  </button>
                </div>
              </Form>

              <div className="text-gray-500 text-sm text-center max-w-md mt-4">
                By clicking Continue, you consent to the verification of your
                identity through your NIN. This information helps us provide you
                with secure wallet services, credit options, and a better
                overall experience.
              </div>
            </div>
          </div>
        )}
      </Formik>
    </div>
  );
};

export default VerifyNIN;
